import { useState } from 'react'
import { Link } from 'react-router-dom'
import type { ColumnDef } from '@tanstack/react-table'
import { MoreHorizontal, Plus, Pencil, Trash2, Building, Eye } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { useOrg } from '@/context/OrgContext'
import { useOrganizations, useDeleteOrganization } from '@/hooks'
import { PageHeader, EmptyState, DataTable, SortableHeader, ConfirmDialog, Forbidden } from '@/components'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { Organization } from '@/types'
import { OrgFormDialog } from './OrgFormDialog'

const planTierLabels: Record<string, string> = {
  free: 'Free',
  starter: 'Starter',
  professional: 'Professional',
  enterprise: 'Enterprise',
}

const planTierVariants: Record<string, 'default' | 'secondary' | 'outline'> = {
  free: 'outline',
  starter: 'secondary',
  professional: 'default',
  enterprise: 'default',
}

export function OrganizationsPage() {
  const { user } = useAuth()
  const { selectOrg } = useOrg()
  const { data: organizations, isLoading, error } = useOrganizations()
  const deleteMutation = useDeleteOrganization()

  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [editOrg, setEditOrg] = useState<Organization | null>(null)
  const [deleteOrg, setDeleteOrg] = useState<Organization | null>(null)

  // Only super_admin can access this page
  if (user?.role !== 'super_admin') {
    return <Forbidden />
  }

  const columns: ColumnDef<Organization>[] = [
    {
      accessorKey: 'name',
      header: ({ column }) => (
        <SortableHeader column={column}>Name</SortableHeader>
      ),
      cell: ({ row }) => (
        <Link
          to={`/organizations/${row.original.id}`}
          className="font-medium text-primary hover:underline"
        >
          {row.getValue('name')}
        </Link>
      ),
    },
    {
      accessorKey: 'contact_email',
      header: ({ column }) => (
        <SortableHeader column={column}>Contact Email</SortableHeader>
      ),
    },
    {
      accessorKey: 'plan_tier',
      header: ({ column }) => (
        <SortableHeader column={column}>Plan</SortableHeader>
      ),
      cell: ({ row }) => {
        const tier = row.getValue('plan_tier') as string
        return (
          <Badge variant={planTierVariants[tier] ?? 'outline'}>
            {planTierLabels[tier] ?? tier}
          </Badge>
        )
      },
    },
    {
      accessorKey: 'created_at',
      header: ({ column }) => (
        <SortableHeader column={column}>Created</SortableHeader>
      ),
      cell: ({ row }) => {
        const date = new Date(row.getValue('created_at'))
        return date.toLocaleDateString()
      },
    },
    {
      id: 'actions',
      cell: ({ row }) => {
        const org = row.original

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => selectOrg(org)}>
                <Eye className="mr-2 h-4 w-4" />
                View as Org
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setEditOrg(org)}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setDeleteOrg(org)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
    },
  ]

  if (error) {
    return (
      <div className="text-destructive">
        Failed to load organizations: {error.message}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Organizations"
        description="Manage all organizations in the system"
        actions={
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Organization
          </Button>
        }
      />

      {isLoading ? (
        <div className="text-muted-foreground">Loading organizations...</div>
      ) : organizations && organizations.length > 0 ? (
        <DataTable
          columns={columns}
          data={organizations}
          filterColumn="name"
          filterPlaceholder="Filter by name..."
        />
      ) : (
        <EmptyState
          icon={<Building className="h-12 w-12" />}
          title="No organizations"
          description="Get started by creating your first organization."
          action={
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Organization
            </Button>
          }
        />
      )}

      <OrgFormDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />

      {editOrg && (
        <OrgFormDialog
          open={!!editOrg}
          onOpenChange={(open) => !open && setEditOrg(null)}
          organization={editOrg}
        />
      )}

      <ConfirmDialog
        open={!!deleteOrg}
        onOpenChange={(open) => !open && setDeleteOrg(null)}
        title="Delete Organization"
        description={`Are you sure you want to delete "${deleteOrg?.name}"? This will delete all buildings, rooms, assets, users, and reports associated with this organization. This action cannot be undone.`}
        confirmText="Delete"
        destructive
        onConfirm={() => {
          if (deleteOrg) {
            deleteMutation.mutate(deleteOrg.id, {
              onSuccess: () => setDeleteOrg(null),
            })
          }
        }}
        isLoading={deleteMutation.isPending}
      />
    </div>
  )
}
